import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BionicText } from '../components/custom/BionicText';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Plus, 
  Calendar, 
  ShoppingCart, 
  ChefHat,
  ArrowRight,
  Trash2,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const { user, token, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const bgImages = {
    herbs: "https://images.unsplash.com/photo-1515586838455-8f8e37eb37db?w=1920&q=80",
  };

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/auth');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (token) {
      fetchPlans();
    }
  }, [token]);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/diet-plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlans(response.data);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
      toast.error('Failed to load your diet plans');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Are you sure you want to delete this plan?')) return;

    try {
      await axios.delete(`${API_URL}/api/diet-plans/${planId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlans(plans.filter(p => p.plan_id !== planId));
      toast.success('Plan deleted');
    } catch (error) {
      toast.error('Failed to delete plan');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <main className="min-h-screen pt-24 pb-12 px-4 relative">
      {/* B&W Herb Background */}
      <div 
        className="fixed inset-0 bg-cover bg-center -z-10"
        style={{ 
          backgroundImage: `url(${bgImages.herbs})`,
          filter: 'grayscale(100%) contrast(1.05) brightness(0.95)'
        }}
        aria-hidden="true" 
      />
      <div className="fixed inset-0 bg-background/95 -z-10" aria-hidden="true" />
      
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8 opacity-0 animate-fade-in">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground">
              <BionicText>Welcome back!</BionicText>
            </h1>
            <p className="text-muted-foreground mt-1">
              {user?.email}
            </p>
          </div>
          <Button 
            onClick={() => navigate('/diet-builder')}
            className="rounded-full gap-2"
            data-testid="create-plan-btn"
          >
            <Plus className="h-5 w-5" aria-hidden="true" />
            Create New Plan
          </Button>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <Card 
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate('/diet-builder')}
            data-testid="quick-action-create"
          >
            <CardContent className="pt-6 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-primary/10">
                <Calendar className="h-6 w-6 text-primary" aria-hidden="true" />
              </div>
              <div>
                <h3 className="font-semibold text-card-foreground">New Meal Plan</h3>
                <p className="text-sm text-muted-foreground">Create a diet plan</p>
              </div>
              <ArrowRight className="h-5 w-5 text-muted-foreground ml-auto" aria-hidden="true" />
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate('/grocery-list')}
            data-testid="quick-action-grocery"
          >
            <CardContent className="pt-6 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-accent/10">
                <ShoppingCart className="h-6 w-6 text-accent" aria-hidden="true" />
              </div>
              <div>
                <h3 className="font-semibold text-card-foreground">Grocery List</h3>
                <p className="text-sm text-muted-foreground">View & compare prices</p>
              </div>
              <ArrowRight className="h-5 w-5 text-muted-foreground ml-auto" aria-hidden="true" />
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate('/settings')}
            data-testid="quick-action-settings"
          >
            <CardContent className="pt-6 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-secondary">
                <ChefHat className="h-6 w-6 text-secondary-foreground" aria-hidden="true" />
              </div>
              <div>
                <h3 className="font-semibold text-card-foreground">Preferences</h3>
                <p className="text-sm text-muted-foreground">Update your settings</p>
              </div>
              <ArrowRight className="h-5 w-5 text-muted-foreground ml-auto" aria-hidden="true" />
            </CardContent>
          </Card>
        </div>

        {/* Diet Plans */}
        <section aria-labelledby="plans-heading">
          <h2 id="plans-heading" className="font-heading text-2xl font-semibold text-foreground mb-4">
            <BionicText>Your Diet Plans</BionicText>
          </h2>

          {plans.length === 0 ? (
            <Card data-testid="no-plans-card">
              <CardContent className="py-12 text-center">
                <div className="mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                  <Calendar className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
                </div>
                <h3 className="font-semibold text-lg text-card-foreground mb-2">
                  No diet plans yet
                </h3>
                <p className="text-muted-foreground mb-6">
                  Create your first personalized diet plan to get started.
                </p>
                <Button 
                  onClick={() => navigate('/diet-builder')}
                  className="rounded-full gap-2"
                  data-testid="create-first-plan-btn"
                >
                  <Plus className="h-5 w-5" aria-hidden="true" />
                  Create Your First Plan
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {plans.map((plan, index) => (
                <Card 
                  key={plan.plan_id} 
                  className="hover:shadow-lg transition-shadow opacity-0 animate-fade-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`plan-card-${plan.plan_id}`}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{plan.name}</CardTitle>
                        <CardDescription>
                          {plan.days} days • {plan.servings} servings
                        </CardDescription>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeletePlan(plan.plan_id);
                        }}
                        className="text-muted-foreground hover:text-destructive"
                        aria-label="Delete plan"
                        data-testid={`delete-plan-${plan.plan_id}`}
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {plan.diet_types.slice(0, 3).map((diet) => (
                        <Badge key={diet} variant="secondary" className="text-xs">
                          {diet}
                        </Badge>
                      ))}
                      {plan.diet_types.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{plan.diet_types.length - 3}
                        </Badge>
                      )}
                    </div>

                    {plan.allergens.length > 0 && (
                      <p className="text-xs text-muted-foreground mb-4">
                        Avoiding: {plan.allergens.join(', ')}
                      </p>
                    )}

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 rounded-full"
                        onClick={() => navigate(`/meal-plan/${plan.plan_id}`)}
                        data-testid={`view-plan-${plan.plan_id}`}
                      >
                        {plan.meal_plan ? 'View Plan' : 'Generate Meals'}
                      </Button>
                      {plan.grocery_list && (
                        <Button
                          size="sm"
                          className="rounded-full"
                          onClick={() => navigate(`/grocery-list/${plan.plan_id}`)}
                          data-testid={`view-grocery-${plan.plan_id}`}
                        >
                          <ShoppingCart className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
